import { ColDef } from "ag-grid-community";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ElementSales, ProductsReportResponse } from "./ProductsSoldReportModal";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { MDBIcon } from "mdb-react-ui-kit";
import { AgGridReact } from "ag-grid-react";

export default function ProductsSoldReportTable({
    data
}: {
    data: ProductsReportResponse
}){
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();
    const isMobile = useMemo(() => innerWidth < 600, [innerWidth]);

    const totalsRow = useMemo<ElementSales>(() => {
        return {
            names: [t('total')],
            drops: data.values.reduce((prev, crt) => {
                crt.drops.forEach((drop, index) => {
                    prev[index] = (prev[index] ?? 0) + drop;
                });
                return prev;
            }
            , [] as number[]),
            isExtra: false,
            id: -1
        }
    }, [data.values, t]);

    const absoluteTotal = useMemo<number>(() => {
        return data.values.reduce((prev, crt) => {
            crt.drops.forEach((drop, index) => {
                prev[index] = (prev[index] ?? 0) + drop;
            });
            return prev;
        }, [] as number[]).reduce((prev, crt) => prev + crt, 0);
    }, [data.values]);

    const columnDefs = useMemo<ColDef<ElementSales>[]>(() => ([
        {
            headerName: t('name'),
            valueGetter: row => row.data?.names[0],
            width: 250,
            resizable: true,
            sortable: true,
            pinned: isMobile ? undefined : 'left',
            tooltipValueGetter: row => row.data?.names.splice(1).join(', '),
            cellRenderer: ({value, data, node}) => (
                <span>
                    <span style={{fontWeight: node.rowPinned ? 'bold' : ''}}>{value}</span>
                    {data.names.length > 1 && <span className="ms-1 text-warning"><MDBIcon icon="asterisk" size="xs"/></span>}
                </span>
            )
        },
        {
            headerName: t('total'),
            valueGetter: row => row.data?.drops.reduce((prev, crt) => prev + crt, 0),
            width: 150,
            resizable: true,
            sortable: true,
            pinned: isMobile ? undefined : 'right',
            cellRenderer: ({value, node}) => {
                const avg = absoluteTotal / data.values.length;
                const ratio = value / avg;

                let color = '';
                if (ratio < 1){
                    color = `rgba(255,0,0,${0.5 * (1-ratio)})`
                }else{
                    color = `rgba(0,255,0,${Math.min(0.8, 0.5 * (ratio-1))})`;
                }

                return (
                    <div className="d-flex flex-row justify-content-center h-100 w-100 align-items-center px-3" style={{background: node.rowPinned ? '' : color}}>
                        <span className="d-flex flex-fill">{value}</span>
                        {(!node.rowPinned && ratio !== null && !value.hidePercent) && <span className="ms-2 text-muted" style={{fontSize: '0.65rem'}}>{(ratio*100).toFixed(1)}%</span>}
                    </div>
                )
            },
        },
        ...(data.products.map(product => ({
            headerName: product.name,
            valueGetter: row => row.data?.drops[data.products.findIndex(p => p.id === product.id)],
            headerStyle: {writingMode: data.products.length > 10 ? 'vertical-lr' : 'horizontal-tb'},
            width: data.products.length > 10 ? 100 : 150,
            resizable: true,
            sortable: true
        }) satisfies ColDef<ElementSales>))
    ]), [t, isMobile, absoluteTotal, data.products, data.values]);

    return (
        <AgGridReact<ElementSales>
            columnDefs={columnDefs}
            enableBrowserTooltips
            rowData={data.values}
            pinnedBottomRowData={[totalsRow]}
            headerHeight={150}
        />
    )
}